import React, { useState, useEffect } from "react";
import OptimizedImg from "../../components/OptimizedImg";

/* ============================================================
   HOW THIS PAGE WAS BUILT — task list

   1. Define brand tokens & fonts
      Set up the color palette (the C object below: backgrounds,
      borders, text, accent colors) and the font stack (FF) once
      at the top of the file. Every component reads from these so
      the whole page can be re-themed by editing a handful of
      values instead of hunting through markup.

   2. Draft the content model
      Put all copy, feature lists, testimonials and FAQ entries into
      one DATA object. Keeping content separate from layout makes it
      easy to update text without touching JSX.

   3. Build small reusable pieces
      Section, Kicker, H2, and card variants (PassCard, IndexCard,
      ScheduleRow) apply consistent spacing, borders and type
      wherever they're used, instead of repeating inline styles.

   4. Build the hero
      Tagline, description, CTA buttons (mailto + contact link)
      and a QR-pass badge, paired with a phone mockup on the side
      so the product is visible immediately.

   5. Build the phone mockups
      A PhoneFrame wrapper plus inner screens (course list, video
      player, quiz) built from plain divs and SVG — no real
      screenshots needed to show the app's UI.

   6. Build the content sections
      Benefits grid, module grid, how-it-works schedule, platform
      card, testimonials and FAQ accordion, each wrapped in Section
      with alternating background bands.

   7. Add interactions
      Hover states on cards/buttons, a scrolling course ticker, and
      a click-to-expand FAQ, all via local component state.

   8. Make it responsive
      One <style> block with class-based media queries at 980px,
      760px, 640px and 620px collapsing grids and tightening
      section padding.

   9. Finish with a CTA
      A boarding-pass-style close — dark background, a light card
      with demo/call buttons and a QR glyph — mirroring the hero's
      badge for visual bookending.

   ── UPDATE (previous revision) ──
   Buttons and heading typography brought in line with the CardFlow
   (project 3) page's system:
     - Buttons: borderRadius bumped from 3px to 10px (CardFlow's
       pill-ish rounded corners instead of the sharp boarding-pass
       edges), display:inline-block added, and a translateY(-2px)
       lift-on-hover added to every button (hero + final CTA),
       matching CardFlow's hover pattern on its "Request a Demo +"
       / "Call Us" buttons.
     - Headings: H2 letterSpacing tightened from -0.3px to -0.5px
       (CardFlow's SectionHeading value), and the hero <h1> bumped
       from fontWeight 700 / letterSpacing -0.5px to fontWeight 800 /
       letterSpacing -1px, matching CardFlow's .cf-hero-heading.
   Card shapes, colors and everything else are unchanged.

   ── UPDATE (this revision) ──
   Hero background image added.
     - New image slot IMG_HERO ("/images/lms/h.png") is now a
       full-bleed background behind the entire hero section, the
       same pattern used on the CardFlow / Samone / Hospital
       Equipment hero sections: an <img> covering the section
       edge-to-edge with a dark (C.ink) scrim gradient over it for
       text contrast, rather than fading into the page background.
     - Because the hero now sits on a dark photo instead of the
       light parchment background, hero-only text and controls were
       switched to their light-on-dark equivalents so they stay
       legible: the eyebrow line, heading, description, and QR pass
       badge now use white/cream tones instead of C.ink/C.inkSoft/
       C.paper, and the "Talk to us" outline button uses a white
       border instead of a dark one. The tagline2 accent word keeps
       its existing #6C0A9F color, with a thin white text-stroke
       and soft shadow added so it stays readable over the photo.
     - Nothing outside the hero section (ticker, passes, modules,
       schedule, phones, notes, FAQ, final CTA) changed.
   ============================================================ */

/* ─── Image slots ─── */
const IMG_HERO = "/images/project5/h.png"; // full-bleed hero background image

/* ─── Request-a-Demo mailto ─── */
const DEMO_EMAIL = "hr@sagveektech.com";
const DEMO_SUBJECT = "Demo Request - Sagveek LMS";
const DEMO_BODY = [
  "Hi Sagveek Technologies team,",
  "",
  "I would like to request a demo of the Sagveek Learning Management platform.",
  "",
  "Please let me know a suitable time to connect.",
  "",
  "Thanks,",
].join("\n");
const DEMO_MAILTO = `mailto:${DEMO_EMAIL}?subject=${encodeURIComponent(
  DEMO_SUBJECT
)}&body=${encodeURIComponent(DEMO_BODY)}`;

/* ─── Content ─── */
const DATA = {
  courseCode: "LMS · 2026 SEM",
  name: "Sagveek",
  tagline1: "Enroll with a scan.",
  tagline2: "Finish with a certificate.",
  description:
    "A mobile learning app for organizations that teach at scale — course catalogs, video lessons, quizzes and certificates, with a QR pass that gets a learner from the door to their first lesson in seconds.",
  tickerCourses: [
    "UI/UX Foundations", "Public Speaking 101", "Data Analytics", "Electrical Safety",
    "Spoken English", "Workplace First Aid", "Digital Marketing", "Site Supervision",
  ],
  passes: [
    { code: "01", title: "Learn on any device", desc: "Lessons, quizzes and certificates live in one app — nothing to install site-side." },
    { code: "02", title: "One app, every role", desc: "Students, staff, org admins and super admins each open to their own view." },
    { code: "03", title: "Enroll by QR", desc: "Scan a code at orientation and a learner is enrolled, no forms." },
    { code: "04", title: "Track progress live", desc: "See who's enrolled, finished, or stalled — across every organization." },
  ],
  modules: [
    { code: "MOD.01", title: "Video Course Library", desc: "Stream lessons with resumable progress, backed by fast cloud delivery." },
    { code: "MOD.02", title: "QR Enrollment", desc: "Every course or cohort gets a scannable pass that enrolls a learner instantly." },
    { code: "MOD.03", title: "Role-based Access", desc: "Separate workspaces for students, staff, org admins and platform admins." },
    { code: "MOD.04", title: "Quizzes & Grading", desc: "Auto-generated and manual quizzes with instant scoring per course." },
    { code: "MOD.05", title: "Certificates", desc: "Issued automatically the moment a course is completed." },
    { code: "MOD.06", title: "Cart & Org Plans", desc: "Course pricing, checkout, and organization-level subscription plans." },
  ],
  schedule: [
    { n: "01", title: "Browse the catalog", desc: "Learners explore courses by topic, organization, or career goal.", tag: "Self-paced" },
    { n: "02", title: "Enroll", desc: "Join by purchase, invite, or a single QR scan at check-in.", tag: "Instant" },
    { n: "03", title: "Watch & practice", desc: "Stream video lessons with progress saved automatically.", tag: "Resumable" },
    { n: "04", title: "Take the quiz", desc: "Test understanding with auto-graded questions tied to the course.", tag: "Auto-graded" },
    { n: "05", title: "Earn the certificate", desc: "Completion triggers a certificate on the learner's profile.", tag: "Automatic" },
  ],
  catalogCard: [
    { k: "Deployment", v: "iOS & Android app" },
    { k: "Workspaces", v: "Student · Staff · Admin · Org" },
    { k: "Media", v: "Cloud video streaming" },
    { k: "Security", v: "Role-based, encrypted auth" },
    { k: "Support", v: "24/7 phone & email" },
    { k: "Scale", v: "Single org → multi-org network" },
  ],
  notes: [
    { text: "Enrollment used to mean paperwork at the door. Now it's one scan and they're in.", name: "Priya Nair", role: "Program Coordinator, Pune Skills Center" },
    { text: "We finally see who's actually finishing courses, not just who signed up.", name: "Vikram Shah", role: "Training Head, 3 organizations" },
    { text: "Our staff stopped issuing certificates by hand. It just happens now.", name: "Meera Joshi", role: "Academic Admin, Sagveek Learning" },
  ],
  faq: [
    { q: "What is Sagveek LMS?", a: "A mobile learning app where organizations publish courses and learners watch lessons, take quizzes and earn certificates, all from one app." },
    { q: "How does QR enrollment work?", a: "Every course or cohort gets a unique QR code. Scanning it enrolls the learner immediately — no separate registration step." },
    { q: "Can one platform support multiple organizations?", a: "Yes. Organizations run their own catalogs, staff and students, while a super admin oversees the network from one place." },
    { q: "Is learner data secure?", a: "Yes — access is role-based and encrypted, with activity tracked against individual accounts." },
  ],
  quizQuestion: "Which of these is checked automatically?",
  quizOptions: ["Attendance sheet", "Quiz score", "Printed certificate", "Enrollment form"],
};

/* ─── Tokens — matched to the CardFlow / Sagveek brand system:
   off-white bg, deep-purple structural accent, magenta primary
   accent, near-black purple for dark sections. ─── */
const C = {
  ink: "#12061F",
  inkSoft: "#5A4E68",
  parchment: "#FAF8FC",
  paper: "#FFFFFF",
  paperDim: "#F5EEFA",
  rule: "#E6DCEF",
  clay: "#5A4E68",
  marigold: "#5A4E68",
  marigoldD: "#9E1281",
  moss: "#3D1259",
  mossD: "#2A0D40",
  cream: "#F1EAF6",
};

const FF = {
  display: "'IBM Plex Sans', system-ui, sans-serif",
  body: "'IBM Plex Sans', system-ui, sans-serif",
  mono: "'IBM Plex Mono', monospace",
};

/* ─── Small pieces ─── */
function QRGlyph({ size = 34, color = C.ink }) {
  const cells = [
    1,1,1,0,1,0,1,1,1,
    1,0,1,0,0,0,1,0,1,
    1,0,1,0,1,0,1,0,1,
    0,0,0,0,1,0,0,0,0,
    1,0,1,1,0,1,1,0,1,
    0,0,0,0,1,0,0,0,0,
    1,0,1,0,1,0,1,0,1,
    1,0,1,0,0,0,1,0,1,
    1,1,1,0,1,0,1,1,1,
  ];
  const cell = size / 9;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      {cells.map((c, i) =>
        c ? (
          <rect key={i} x={(i % 9) * cell} y={Math.floor(i / 9) * cell} width={cell} height={cell} fill={color} />
        ) : null
      )}
    </svg>
  );
}

function Section({ children, style, className = "" }) {
  return (
    <section className={`lms2-section ${className}`} style={{ maxWidth: 1160, margin: "0 auto", padding: "84px 28px", ...style }}>
      {children}
    </section>
  );
}

function Kicker({ children }) {
  return (
    <div style={{ fontFamily: FF.mono, fontSize: 12.5, color: C.moss, letterSpacing: "0.02em", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ width: 14, height: 1.5, background: C.moss, display: "inline-block" }} />
      {children}
    </div>
  );
}

function H2({ children }) {
  return (
    <h2 style={{ fontFamily: FF.display, fontWeight: 700, fontSize: "clamp(28px, 3.4vw, 40px)", color: C.ink, margin: "0 0 18px", letterSpacing: "-0.5px", lineHeight: 1.2 }}>
      {children}
    </h2>
  );
}

function PassCard({ code, title, desc, compact }) {
  return (
    <div style={{
      position: "relative", background: C.paper, border: `1.5px solid ${C.rule}`, borderRadius: 4,
      padding: compact ? "18px 18px 16px" : "22px 22px 20px", display: "flex", flexDirection: "column", gap: 10,
    }}>
      <div style={{ position: "absolute", top: "50%", left: -8, width: 16, height: 16, borderRadius: "50%", background: C.parchment, border: `1.5px solid ${C.rule}`, borderRight: "none", transform: "translateY(-50%)" }} />
      <div style={{ position: "absolute", top: "50%", right: -8, width: 16, height: 16, borderRadius: "50%", background: C.parchment, border: `1.5px solid ${C.rule}`, borderLeft: "none", transform: "translateY(-50%)" }} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontFamily: FF.mono, fontSize: 11.5, color: C.marigoldD, fontWeight: 600 }}>NO. {code}</span>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.moss }} />
      </div>
      <h3 style={{ fontFamily: FF.display, fontWeight: 700, fontSize: 17.5, color: C.ink, margin: 0 }}>{title}</h3>
      <p style={{ fontFamily: FF.body, fontSize: 13.5, color: C.clay, margin: 0, lineHeight: 1.6 }}>{desc}</p>
    </div>
  );
}

function IndexCard({ code, title, desc }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: C.paper, border: `1.5px solid ${hov ? C.moss : C.rule}`, borderRadius: 3,
        padding: "22px 22px 20px", position: "relative", transition: "border-color 0.2s ease, transform 0.2s ease",
        transform: hov ? "translateY(-3px)" : "none",
      }}
    >
      <div style={{ position: "absolute", top: -1, left: 20, width: 46, height: 8, background: C.marigold, borderRadius: "0 0 3px 3px" }} />
      <div style={{ fontFamily: FF.mono, fontSize: 11, color: C.moss, letterSpacing: "0.03em", marginBottom: 12, marginTop: 6 }}>{code}</div>
      <h3 style={{ fontFamily: FF.display, fontWeight: 700, fontSize: 18, color: C.ink, margin: "0 0 8px" }}>{title}</h3>
      <p style={{ fontFamily: FF.body, fontSize: 13.5, color: C.clay, margin: 0, lineHeight: 1.65 }}>{desc}</p>
    </div>
  );
}

function ScheduleRow({ n, title, desc, tag, last }) {
  return (
    <div className="lms2-schedule-row" style={{
      display: "grid", gridTemplateColumns: "52px 1fr auto", gap: 18, alignItems: "start",
      padding: "22px 4px", borderBottom: last ? "none" : `1px solid ${C.rule}`,
    }}>
      <div style={{ fontFamily: FF.display, fontSize: 22, fontWeight: 700, color: C.marigoldD }}>{n}</div>
      <div>
        <h4 style={{ fontFamily: FF.display, fontWeight: 700, fontSize: 16.5, color: C.ink, margin: "0 0 5px" }}>{title}</h4>
        <p style={{ fontFamily: FF.body, fontSize: 13.5, color: C.clay, margin: 0, lineHeight: 1.6, maxWidth: 480 }}>{desc}</p>
      </div>
      <div style={{ fontFamily: FF.mono, fontSize: 11, color: C.moss, border: `1px solid ${C.moss}55`, borderRadius: 20, padding: "4px 12px", height: "fit-content", whiteSpace: "nowrap" }}>{tag}</div>
    </div>
  );
}

/* ─── Phone mockups ─── */
function PhoneFrame({ children, tiltDeg = 0 }) {
  return (
    <div style={{
      width: 252, height: 512, background: C.ink, borderRadius: 34, padding: 9,
      boxShadow: "0 30px 60px rgba(18,6,31,0.28), 0 6px 16px rgba(18,6,31,0.18)",
      transform: tiltDeg ? `rotate(${tiltDeg}deg)` : "none", flexShrink: 0,
    }}>
      <div style={{ width: "100%", height: "100%", background: C.paper, borderRadius: 26, overflow: "hidden", position: "relative", display: "flex", flexDirection: "column" }}>
        <div style={{ position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)", width: 74, height: 18, background: C.ink, borderRadius: 12, zIndex: 2 }} />
        <div style={{ padding: "16px 16px 8px", display: "flex", justifyContent: "space-between", fontFamily: FF.mono, fontSize: 10, color: C.clay }}>
          <span>9:41</span>
          <span>●●● 100%</span>
        </div>
        <div style={{ flex: 1, overflow: "hidden" }}>{children}</div>
      </div>
    </div>
  );
}

function CourseListScreen() {
  const courses = [
    { title: "UI/UX Foundations", pct: 72, tag: "Design" },
    { title: "Public Speaking 101", pct: 40, tag: "Soft Skills" },
    { title: "Site Supervision", pct: 12, tag: "Industrial" },
  ];
  return (
    <div style={{ padding: "8px 16px 16px" }}>
      <div style={{ fontFamily: FF.display, fontWeight: 700, fontSize: 17, color: C.ink, marginBottom: 4 }}>My learning</div>
      <div style={{ fontFamily: FF.body, fontSize: 11, color: C.clay, marginBottom: 14 }}>3 courses in progress</div>
      {courses.map((c) => (
        <div key={c.title} style={{ background: C.parchment, border: `1px solid ${C.rule}`, borderRadius: 10, padding: "12px 12px", marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ fontFamily: FF.body, fontWeight: 600, fontSize: 12.5, color: C.ink }}>{c.title}</span>
            <span style={{ fontFamily: FF.mono, fontSize: 9.5, color: C.moss }}>{c.tag}</span>
          </div>
          <div style={{ height: 5, background: C.rule, borderRadius: 3, overflow: "hidden" }}>
            <div style={{ width: `${c.pct}%`, height: "100%", background: C.marigold }} />
          </div>
          <div style={{ fontFamily: FF.mono, fontSize: 9.5, color: C.clay, marginTop: 5 }}>{c.pct}% complete</div>
        </div>
      ))}
    </div>
  );
}

function VideoScreen() {
  return (
    <div style={{ padding: "8px 16px 16px" }}>
      <div style={{ background: C.ink, borderRadius: 10, height: 130, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12, position: "relative" }}>
        <div style={{ width: 0, height: 0, borderTop: "9px solid transparent", borderBottom: "9px solid transparent", borderLeft: `14px solid ${C.cream}`, marginLeft: 4 }} />
        <div style={{ position: "absolute", bottom: 8, left: 10, right: 10, height: 3, background: "rgba(255,255,255,0.25)", borderRadius: 2 }}>
          <div style={{ width: "58%", height: "100%", background: C.marigold, borderRadius: 2 }} />
        </div>
      </div>
      <div style={{ fontFamily: FF.mono, fontSize: 9.5, color: C.moss, marginBottom: 4 }}>UI/UX FOUNDATIONS · LESSON 4</div>
      <div style={{ fontFamily: FF.display, fontWeight: 700, fontSize: 15, color: C.ink, marginBottom: 12 }}>Working with grid systems</div>
      {["Lesson 5 · Color theory", "Lesson 6 · Typography basics"].map((l) => (
        <div key={l} style={{ fontFamily: FF.body, fontSize: 11.5, color: C.clay, padding: "8px 0", borderTop: `1px solid ${C.rule}` }}>{l}</div>
      ))}
    </div>
  );
}

function QuizScreen({ question, options }) {
  return (
    <div style={{ padding: "8px 16px 16px" }}>
      <div style={{ fontFamily: FF.mono, fontSize: 9.5, color: C.moss, marginBottom: 8 }}>QUIZ · QUESTION 3 OF 8</div>
      <div style={{ fontFamily: FF.display, fontWeight: 700, fontSize: 14, color: C.ink, marginBottom: 12, lineHeight: 1.4 }}>{question}</div>
      {options.map((o, i) => (
        <div key={o} style={{
          display: "flex", alignItems: "center", gap: 8, padding: "9px 10px", marginBottom: 7,
          border: `1px solid ${i === 1 ? C.moss : C.rule}`, borderRadius: 8, background: i === 1 ? `${C.marigold}15` : C.parchment,
        }}>
          <div style={{ width: 14, height: 14, borderRadius: "50%", border: `1.5px solid ${i === 1 ? C.moss : C.clay}`, background: i === 1 ? C.moss : "transparent" }} />
          <span style={{ fontFamily: FF.body, fontSize: 11.5, color: C.ink }}>{o}</span>
        </div>
      ))}
      <div style={{ marginTop: 10, padding: "10px 12px", background: C.moss, borderRadius: 8, textAlign: "center" }}>
        <span style={{ fontFamily: FF.body, fontWeight: 600, fontSize: 12, color: C.cream }}>Certificate issued on completion</span>
      </div>
    </div>
  );
}

/* ─── Page ─── */
const SagveekLMSProductPage = () => {
  const [openFaq, setOpenFaq] = useState(0);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');
        .lms2-root, .lms2-root *{ box-sizing:border-box; }
        .lms2-hero-wrap{ position:relative; overflow:hidden; }
        .lms2-hero-bg{ position:absolute; inset:0; z-index:0; background:${C.ink}; }
        .lms2-hero-bg picture, .lms2-hero-bg img{ width:100%; height:100%; display:block; }
        .lms2-hero-bg img{ object-fit:cover; }
        .lms2-hero-grid{ position:relative; z-index:1; display:grid; grid-template-columns: 1.1fr 0.9fr; gap:40px; align-items:center; }
        .lms2-pass-row{ display:grid; grid-template-columns:repeat(4,1fr); gap:22px 20px; }
        .lms2-module-grid{ display:grid; grid-template-columns:repeat(3,1fr); gap:18px; }
        .lms2-note-grid{ display:grid; grid-template-columns:repeat(3,1fr); gap:20px; }
        .lms2-phones-row{ display:flex; gap:34px; justify-content:center; align-items:flex-end; }
        .lms2-catalog-grid{ display:grid; grid-template-columns:1.05fr 0.95fr; gap:56px; align-items:center; }
        @keyframes lms2-marquee{ from{ transform:translateX(0);} to{ transform:translateX(-50%);} }
        .lms2-ticker-track{ display:flex; width:max-content; animation: lms2-marquee 26s linear infinite; }
        @media(prefers-reduced-motion:reduce){ .lms2-ticker-track{ animation:none; } }
        .lms2-cta-grid{ display:grid; grid-template-columns: 1fr auto; }

        @media (max-width: 980px){
          .lms2-hero-grid{ grid-template-columns:1fr; }
          .lms2-catalog-grid{ grid-template-columns:1fr; gap:36px; }
          .lms2-phones-row{ flex-wrap:wrap; }
          .lms2-cta-grid{ grid-template-columns:1fr; }
        }
        @media (max-width: 760px){
          .lms2-pass-row{ grid-template-columns:repeat(2,1fr); }
          .lms2-module-grid{ grid-template-columns:1fr; }
          .lms2-note-grid{ grid-template-columns:1fr; }
        }
        @media (max-width: 640px){
          .lms2-schedule-row{ grid-template-columns: 40px 1fr !important; }
          .lms2-schedule-row > div:last-child{ grid-column: 1 / -1; margin-top:6px; }
        }
        @media (max-width: 620px){
          .lms2-section{ padding:56px 20px !important; }
        }
      `}</style>

      <div className="lms2-root" style={{ background: C.parchment, color: C.ink, fontFamily: FF.body, minHeight: "100vh", overflowX: "hidden" }}>

        {/* ══ HERO — full-bleed background image with dark scrim, light hero text/controls ══ */}
        <div className="lms2-hero-wrap">
          <div className="lms2-hero-bg">
            <OptimizedImg src={IMG_HERO} alt="Sagveek LMS learners using the app" loading="eager" fetchPriority="high" />
            <div style={{ position: "absolute", inset: 0, background: `linear-gradient(100deg, ${C.ink}EE 0%, ${C.ink}C2 38%, ${C.ink}77 62%, ${C.ink}2E 100%)` }} />
            <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top, ${C.ink}CC 0%, transparent 30%)` }} />
          </div>

          <Section style={{ paddingTop: 64, paddingBottom: 48 }}>
            <div className="lms2-hero-grid">
              <div>
                <div style={{ fontFamily: FF.mono, fontSize: 12.5, color: "#E7D9F0", marginBottom: 22, display: "flex", alignItems: "center", gap: 10 }}>
                  <QRGlyph size={16} color="#E7D9F0" />
                  {DATA.courseCode} — {DATA.name} Learning Platform
                </div>
                <h1 style={{ fontFamily: FF.display, fontWeight: 800, fontSize: "clamp(38px, 5.6vw, 62px)", lineHeight: 1.06, letterSpacing: "-1px", color: "#fff", margin: "0 0 22px", textShadow: "0 2px 14px rgba(0,0,0,0.4)" }}>
                  {DATA.tagline1}
                  <br />
                  <span
                    style={{
                      color: "#6C0A9F",
                      WebkitTextStroke: "0.6px rgba(255,255,255,0.85)",
                      textShadow: "0 1px 12px rgba(0,0,0,0.55)",
                    }}
                  >
                    {DATA.tagline2}
                  </span>
                </h1>
                <p style={{ fontFamily: FF.body, fontSize: 16.5, color: "#D9CFE3", maxWidth: 460, margin: "0 0 34px", lineHeight: 1.7 }}>
                  {DATA.description}
                </p>
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 40 }}>
                  <a href={DEMO_MAILTO} style={{
                    fontFamily: FF.body, fontWeight: 600, fontSize: 14.5, padding: "13px 26px", background: "#6C0A9F", color: "#fff",
                    textDecoration: "none", borderRadius: 10, border: "1.5px solid #6C0A9F", transition: "all 0.2s", display: "inline-block",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#56087F"; e.currentTarget.style.borderColor = "#56087F"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#6C0A9F"; e.currentTarget.style.borderColor = "#6C0A9F"; e.currentTarget.style.transform = "none"; }}
                  >Request a demo</a>
                  <a href="/contact" style={{
                    fontFamily: FF.body, fontWeight: 600, fontSize: 14.5, padding: "13px 26px", border: "1.5px solid rgba(255,255,255,0.45)",
                    color: "#fff", textDecoration: "none", borderRadius: 10, transition: "all 0.2s", display: "inline-block", background: "rgba(255,255,255,0.06)", backdropFilter: "blur(4px)",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.16)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.75)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.45)"; e.currentTarget.style.transform = "none"; }}
                  >Talk to us</a>
                </div>

                <div style={{ display: "inline-flex", alignItems: "center", gap: 16, background: "rgba(255,255,255,0.08)", border: "1.5px dashed rgba(255,255,255,0.35)", borderRadius: 6, padding: "14px 20px", backdropFilter: "blur(6px)" }}>
                  <QRGlyph size={38} color="#fff" />
                  <div>
                    <div style={{ fontFamily: FF.mono, fontSize: 10.5, color: "#E7D9F0", marginBottom: 2 }}>ADMIT ONE · ENROLLMENT PASS</div>
                    <div style={{ fontFamily: FF.display, fontWeight: 700, fontSize: 15, color: "#fff" }}>Scan → enrolled in seconds</div>
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "center" }}>
                <PhoneFrame><CourseListScreen /></PhoneFrame>
              </div>
            </div>
          </Section>
        </div>

        {/* ══ TICKER ══ */}
        <div style={{ borderTop: `1px solid ${C.rule}`, borderBottom: `1px solid ${C.rule}`, background: C.ink, overflow: "hidden", padding: "13px 0" }}>
          <div className="lms2-ticker-track">
            {[...DATA.tickerCourses, ...DATA.tickerCourses].map((t, i) => (
              <span key={i} style={{ fontFamily: FF.mono, fontSize: 12.5, color: C.cream, opacity: 0.75, padding: "0 26px", whiteSpace: "nowrap", borderRight: `1px solid ${C.inkSoft}` }}>{t}</span>
            ))}
          </div>
        </div>

        {/* ══ PASSES (benefits) ══ */}
        <Section>
          <Kicker>Why organizations switch</Kicker>
          <H2>Four reasons this replaces the logbook.</H2>
          <div className="lms2-pass-row" style={{ marginTop: 36 }}>
            {DATA.passes.map((p) => <PassCard key={p.code} {...p} />)}
          </div>
        </Section>

        {/* ══ APP SCREENS ══ */}
        <div style={{ borderTop: `1px solid ${C.rule}`, background: C.paperDim }}>
          <Section>
            <Kicker>Inside the app</Kicker>
            <H2>The whole course, in your pocket.</H2>
            <p style={{ fontFamily: FF.body, fontSize: 15, color: C.clay, maxWidth: 560, margin: "0 0 48px", lineHeight: 1.7 }}>
              From browsing a catalog to watching a lesson to sitting a quiz — every step happens in the same app, on the same phone a learner already carries.
            </p>
            <div className="lms2-phones-row">
              <PhoneFrame tiltDeg={-4}><CourseListScreen /></PhoneFrame>
              <PhoneFrame><VideoScreen /></PhoneFrame>
              <PhoneFrame tiltDeg={4}><QuizScreen question={DATA.quizQuestion} options={DATA.quizOptions} /></PhoneFrame>
            </div>
          </Section>
        </div>

        {/* ══ SCHEDULE (how it works) ══ */}
        <Section>
          <Kicker>How it works</Kicker>
          <H2>Five sessions, start to certificate.</H2>
          <div style={{ marginTop: 28, background: C.paper, border: `1px solid ${C.rule}`, borderRadius: 4, padding: "4px 20px" }}>
            {DATA.schedule.map((s, i) => (
              <ScheduleRow key={s.n} n={s.n} title={s.title} desc={s.desc} tag={s.tag} last={i === DATA.schedule.length - 1} />
            ))}
          </div>
        </Section>

        {/* ══ MODULES ══ */}
        <div style={{ borderTop: `1px solid ${C.rule}`, background: C.paperDim }}>
          <Section>
            <Kicker>What's included</Kicker>
            <H2>Six modules. No feature paywalls.</H2>
            <div className="lms2-module-grid" style={{ marginTop: 36 }}>
              {DATA.modules.map((m) => <IndexCard key={m.code} {...m} />)}
            </div>
          </Section>
        </div>

        {/* ══ CATALOG CARD + STATS ══ */}
        <Section>
          <div className="lms2-catalog-grid">
            <div>
              <Kicker>At a glance</Kicker>
              <H2>Built for a network, not just one classroom.</H2>
              <p style={{ fontFamily: FF.body, fontSize: 15, color: C.clay, margin: 0, lineHeight: 1.75, maxWidth: 480 }}>
                Every organization runs its own catalog, staff and students, while a super admin sees the whole network from one place.
              </p>
            </div>

            <div style={{ background: C.paper, border: `1.5px solid ${C.rule}`, borderRadius: 6, padding: "26px 26px 22px", position: "relative" }}>
              <div style={{ position: "absolute", top: 16, right: 18, width: 10, height: 10, borderRadius: "50%", border: `1.5px solid ${C.rule}`, background: C.parchment }} />
              <div style={{ fontFamily: FF.mono, fontSize: 11, color: C.moss, marginBottom: 18 }}>PLATFORM CARD</div>
              {DATA.catalogCard.map((row, i) => (
                <div key={row.k} style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "11px 0", borderTop: i === 0 ? "none" : `1px dashed ${C.rule}` }}>
                  <span style={{ fontFamily: FF.body, fontSize: 13, color: C.clay }}>{row.k}</span>
                  <span style={{ fontFamily: FF.body, fontWeight: 600, fontSize: 13, color: C.ink, textAlign: "right" }}>{row.v}</span>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* ══ NOTES (testimonials) ══ */}
        <div style={{ borderTop: `1px solid ${C.rule}`, background: C.paperDim }}>
          <Section>
            <Kicker>From the field</Kicker>
            <H2>What staff say after the first term.</H2>
            <div className="lms2-note-grid" style={{ marginTop: 36 }}>
              {DATA.notes.map((n, i) => (
                <div key={n.name} style={{
                  background: C.paper, border: `1px solid ${C.rule}`, borderRadius: 3, padding: "22px 22px 18px",
                  transform: `rotate(${i === 1 ? 0 : i === 0 ? -0.6 : 0.6}deg)`,
                }}>
                  <div style={{ width: 30, height: 10, background: `${C.marigold}55`, marginBottom: 14 }} />
                  <p style={{ fontFamily: FF.display, fontSize: 15.5, color: C.ink, lineHeight: 1.55, margin: "0 0 16px", fontWeight: 600 }}>“{n.text}”</p>
                  <div style={{ fontFamily: FF.mono, fontSize: 11, color: C.clay }}>{n.name} — {n.role}</div>
                </div>
              ))}
            </div>
          </Section>
        </div>

        {/* ══ FAQ ══ */}
        <Section style={{ maxWidth: 780 }}>
          <Kicker>Questions</Kicker>
          <H2>Common questions.</H2>
          <div style={{ marginTop: 28, borderTop: `1px solid ${C.rule}` }}>
            {DATA.faq.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={f.q} style={{ borderBottom: `1px solid ${C.rule}` }}>
                  <div onClick={() => setOpenFaq(open ? -1 : i)} style={{
                    padding: "18px 4px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16,
                  }}>
                    <span style={{ fontFamily: FF.display, fontWeight: 700, fontSize: 16, color: C.ink }}>{f.q}</span>
                    <span style={{ fontFamily: FF.mono, fontSize: 16, color: C.marigoldD, transform: open ? "rotate(45deg)" : "none", transition: "transform 0.2s" }}>+</span>
                  </div>
                  {open && <p style={{ padding: "0 4px 20px", fontFamily: FF.body, fontSize: 14, color: C.clay, lineHeight: 1.75, margin: 0, maxWidth: 620 }}>{f.a}</p>}
                </div>
              );
            })}
          </div>
        </Section>

        {/* ══ FINAL CTA — boarding-pass style ══ */}
        <div style={{ background: C.ink }}>
          <Section style={{ paddingTop: 72, paddingBottom: 80 }}>
            <div className="lms2-cta-grid" style={{
              background: C.paper, borderRadius: 10, overflow: "hidden", boxShadow: "0 30px 70px rgba(0,0,0,0.35)",
            }}>
              <div style={{ padding: "40px 40px" }}>
                <div style={{ fontFamily: FF.mono, fontSize: 11.5, color: C.moss, marginBottom: 14 }}>ADMIT ONE · {DATA.courseCode}</div>
                <h2 style={{ fontFamily: FF.display, fontWeight: 700, fontSize: "clamp(26px, 3.4vw, 38px)", color: C.ink, margin: "0 0 16px", lineHeight: 1.2, letterSpacing: "-0.5px" }}>
                  Ready to put every learner on the record?
                </h2>
                <p style={{ fontFamily: FF.body, fontSize: 14.5, color: C.clay, margin: "0 0 28px", maxWidth: 420, lineHeight: 1.7 }}>
                  Join the organizations already running their learners through Sagveek. A free walkthrough, no commitment.
                </p>
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                  <a href={DEMO_MAILTO} style={{
                    fontFamily: FF.body, fontWeight: 600, fontSize: 14.5, padding: "13px 26px", background: C.moss, color: C.cream,
                    textDecoration: "none", borderRadius: 10, transition: "all 0.2s", display: "inline-block",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(61,18,89,0.3)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}
                  >Book a free demo</a>
                  <a href="tel:+919923208955" style={{
                    fontFamily: FF.body, fontWeight: 600, fontSize: 14.5, padding: "13px 26px", border: `1.5px solid ${C.ink}`,
                    color: C.ink, textDecoration: "none", borderRadius: 10, transition: "all 0.2s", display: "inline-block",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = C.ink; e.currentTarget.style.color = C.cream; e.currentTarget.style.transform = "translateY(-2px)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = C.ink; e.currentTarget.style.transform = "none"; }}
                  >Call us</a>
                </div>
              </div>
              <div style={{ borderLeft: `2px dashed ${C.rule}`, display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 34px", background: C.parchment }}>
                <QRGlyph size={92} />
              </div>
            </div>
          </Section>
        </div>
      </div>
    </>
  );
};

export default SagveekLMSProductPage;